import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import { useWorkspaces } from ".";
import AlertMessage from "../../components/AlertMessage";
import FormProvider from "../../components/forms/FormProvider";
import Input from "../../components/forms/Input";
import { ApiResponse } from "../../utils/api";
import { PlanType, Workspace } from "../../utils/types";

interface Props {
  open: boolean;
  toggleCreateWorkspaceForm: () => void;
  addWorkspace: (name: string) => Promise<
    | ApiResponse<Workspace>
    | {
        message: string;
        status: boolean;
      }
    | undefined
  >;
}

const WorkspaceFormSidebar: React.FC<Props> = ({
  open,
  toggleCreateWorkspaceForm,
  addWorkspace
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { currentWorkspace } = useWorkspaces();

  const isCommunityPlan = useMemo(
    () => currentWorkspace?.active_plan?.type === PlanType.community,
    [currentWorkspace?.active_plan?.type]
  );

  const communityPlanAlertMessage = useMemo(() => {
    if (!isCommunityPlan) return null;

    return (
      <AlertMessage
        className="mt-6 mb-0"
        type="warning"
        title="You can have multiple workspaces but there can only be one community plan workspace. Upgrading or deleting your current community plan workspace allows you to create a new workspace."
      />
    );
  }, [isCommunityPlan]);

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      const apiResponse = await addWorkspace(formValues.name);

      if (apiResponse?.status) {
        toggleCreateWorkspaceForm();
      } else {
        setErrorMessage(apiResponse?.message || "Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [addWorkspace, toggleCreateWorkspaceForm]
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 overflow-hidden z-40"
        open={open}
        onClose={toggleCreateWorkspaceForm}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <FormProvider
                  disabled={isCommunityPlan}
                  classnames={{ wrapper: "h-full" }}
                  onCancel={toggleCreateWorkspaceForm}
                  error={errorMessage}
                  formikProps={{
                    onSubmit,
                    initialValues: { name: "" },
                    validationSchema: Yup.object().shape({
                      name: Yup.string()
                        .min(3, "Please enter at least 3 characters")
                        .required("Required")
                    })
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                >
                  {({ isSubmitting }: FormikProps<FormValues>) => {
                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              New workspace
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={toggleCreateWorkspaceForm}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 sm:px-6">
                            {communityPlanAlertMessage}

                            <div className="space-y-6 pt-6 pb-5">
                              <Field
                                id="name"
                                name="name"
                                label="Name"
                                placeholder="My team name"
                                type="text"
                                component={Input}
                                disabled={isSubmitting || isCommunityPlan}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default WorkspaceFormSidebar;

//
// Utils
//

interface FormValues {
  name: string;
}

const ORDERED_FIELD_NAMES = ["name"];
